import { useMemo } from "react";
import type { Contractor, ContractorOpportunity, Job, Issue, CustomerCredit } from "@/data/types";
import type { MarketplaceOffer } from "@/data/contractor/marketplaceOffer";
import { getContractorOfferMetrics } from "@/data/contractor/offerMetrics";

export interface ContractorScorecard {
  contractor: Contractor;
  totalOpps: number;
  responded: number;
  accepted: number;
  rejected: number;
  bids: number;
  noResponse: number;
  responseRate: number;
  acceptanceRate: number;
  rejectionRate: number;
  bidParticipationRate: number;
  assignedJobCount: number;
  completedJobCount: number;
  totalIssues: number;
  openIssues: number;
  issueRate: number;
  recentActivity: string;
  // Credit accountability
  totalCredits: number;
  creditAmount: number;
  avgCreditPerJob: number;
}

export function computeScorecard(
  contractor: Contractor | null | undefined,
  opportunities: ContractorOpportunity[],
  jobs: Job[],
  issues: Issue[],
  credits: CustomerCredit[] = [],
  /**
   * P2: Marketplace offers feed into the SAME acceptanceRate / totalOpps
   * inputs that drive getContractorReliability. We do NOT introduce a new
   * scoring system. accepted/declined offers count like accepted/rejected
   * legacy opportunities; expired offers count as no-response.
   * Withdrawn + superseded are excluded (not contractor behavior).
   */
  marketplaceOffers: MarketplaceOffer[] = [],
): ContractorScorecard {
  const safeContractor = contractor ?? ({
    id: "unknown",
    companyName: "Unknown Contractor",
    contactName: "Unknown Contractor",
    phone: "",
    email: "",
    serviceArea: "",
    serviceRegions: [],
    tradeType: "",
    active: false,
    rating: 0,
    responsivenessRating: 0,
    notes: "",
    insuranceOnFile: false,
    insuranceExpiry: "",
    w9OnFile: false,
    licenseNumber: "",
    complianceNotes: "",
    onboardingStatus: "Under Review",
    agreementSigned: false,
    tags: [],
    branch: "",
    joinedDate: "",
    availabilityStatus: "Unavailable",
    maxWeeklyJobs: 0,
    serviceZipCodes: [],
  } as Contractor);

  const safeOpportunities = opportunities.filter(Boolean);
  const safeJobs = jobs.filter(Boolean);
  const safeIssues = issues.filter(Boolean);
  const safeCredits = credits.filter(Boolean);
  const safeMarketplaceOffers = marketplaceOffers.filter(Boolean);

  const cOpps = safeOpportunities.filter((o) => o.contractorId === safeContractor.id);
  const legacyAccepted = cOpps.filter((o) => o.responseStatus === "Accepted").length;
  const legacyRejected = cOpps.filter((o) => o.responseStatus === "Rejected").length;
  const legacyBids = cOpps.filter((o) => o.responseStatus === "Bid Submitted").length;
  const legacyNoResponse = cOpps.filter((o) => o.responseStatus === "No Response").length;

  const opportunityStatusByJobId = new Map<string, "accepted" | "declined" | "expired" | "pending">();
  for (const opp of cOpps) {
    const mapped =
      opp.responseStatus === "Accepted"
        ? "accepted"
        : opp.responseStatus === "Rejected"
          ? "declined"
          : opp.responseStatus === "No Response"
            ? "expired"
            : "pending";
    const existing = opportunityStatusByJobId.get(opp.jobId);
    const rank = (s: string) =>
      s === "accepted" ? 4 :
      s === "declined" ? 3 :
      s === "expired" ? 2 : 1;
    if (!existing || rank(mapped) > rank(existing)) {
      opportunityStatusByJobId.set(opp.jobId, mapped);
    }
  }

  // Canonicalize status from audit trail first to keep parity with
  // ContractorOfferHistoryPanel "effective status" rendering.
  const canonicalOffers = safeMarketplaceOffers.map((offer) => {
    const audit = Array.isArray(offer.audit) ? offer.audit : [];
    const hasAccepted = audit.some((a) => a?.event === "offer_accepted");
    const hasDeclined = audit.some((a) => a?.event === "offer_declined");
    const hasExpired = audit.some((a) => a?.event === "offer_expired");
    const status = hasAccepted
      ? "accepted"
      : hasDeclined
        ? "declined"
        : hasExpired
          ? "expired"
          : offer.status === "pending" && opportunityStatusByJobId.has(offer.jobId)
            ? opportunityStatusByJobId.get(offer.jobId) ?? offer.status
            : offer.status;
    return { ...offer, status };
  });
  const cOffers = canonicalOffers.filter((o) => o.contractorId === safeContractor.id);

  // If contractor has offer-stream data, use it as single source of truth
  // for offer KPIs so Performance Scorecard reconciles with Offer History.
  const hasOfferSignal = cOffers.length > 0;
  const offerMetrics = getContractorOfferMetrics(safeContractor.id, canonicalOffers, safeJobs, []);
  const acceptedFromOffers = offerMetrics.counts.accepted;
  const rejectedFromOffers = offerMetrics.counts.declined;
  const expiredFromOffers = offerMetrics.counts.expired;
  const pendingFromOffers = cOffers.filter((o) => o.status === "pending").length;
  const withdrawnFromOffers = cOffers.filter((o) => o.status === "withdrawn").length;
  const supersededFromOffers = cOffers.filter((o) => o.status === "superseded").length;

  const accepted = hasOfferSignal ? acceptedFromOffers : legacyAccepted;
  const rejected = hasOfferSignal ? rejectedFromOffers : legacyRejected;
  const bids = hasOfferSignal ? 0 : legacyBids;
  const noResponse = hasOfferSignal ? expiredFromOffers + pendingFromOffers : legacyNoResponse;
  const totalOpps = hasOfferSignal
    ? (offerMetrics.sampleSize + pendingFromOffers + withdrawnFromOffers + supersededFromOffers)
    : cOpps.length;
  const responded = hasOfferSignal
    ? acceptedFromOffers + rejectedFromOffers
    : accepted + rejected + bids;

  const assignedJobs = safeJobs.filter((j) => {
    const idMatch = j.assignedSubcontractorId === safeContractor.id;
    if (idMatch) return true;
    // Fallback for legacy rows that only persisted company name.
    const nameMatch =
      !j.assignedSubcontractorId &&
      !!j.assignedSubcontractor &&
      j.assignedSubcontractor === safeContractor.companyName;
    return nameMatch;
  });
  const completedJobs = assignedJobs.filter((j) => j.currentStage === "Completed");
  const jobIssues = safeIssues.filter((i) => assignedJobs.some((j) => j.id === i.jobId));
  const openIssues = jobIssues.filter((i) => i.status === "Open" || i.status === "In Progress");

  // Credit accountability
  const contractorCredits = safeCredits.filter((c) => c.relatedContractorId === safeContractor.id);
  const totalCreditAmount = contractorCredits.reduce((s, c) => s + c.creditAmount, 0);

  const oppDates = cOpps.filter((o) => o.responseDate).map((o) => o.responseDate);
  const jobDates = assignedJobs.flatMap((j) =>
    j.activityHistory.length > 0 ? [j.activityHistory[j.activityHistory.length - 1].date] : []
  );
  const allDates = [...oppDates, ...jobDates].sort().reverse();

  return {
    contractor: safeContractor,
    totalOpps,
    responded,
    accepted,
    rejected,
    bids,
    noResponse,
    responseRate: totalOpps > 0 ? Math.round((responded / totalOpps) * 100) : 0,
    acceptanceRate: hasOfferSignal
      ? Math.round((offerMetrics.acceptanceRate ?? 0) * 100)
      : (responded > 0 ? Math.round(((accepted + bids) / responded) * 100) : 0),
    rejectionRate: hasOfferSignal
      ? Math.round((offerMetrics.declineRate ?? 0) * 100)
      : (responded > 0 ? Math.round((rejected / responded) * 100) : 0),
    bidParticipationRate: totalOpps > 0 ? Math.round((bids / totalOpps) * 100) : 0,
    assignedJobCount: assignedJobs.length,
    completedJobCount: completedJobs.length,
    totalIssues: jobIssues.length,
    openIssues: openIssues.length,
    issueRate: assignedJobs.length > 0 ? Math.round((jobIssues.length / assignedJobs.length) * 100) / 100 : 0,
    recentActivity: allDates[0] || "—",
    totalCredits: contractorCredits.length,
    creditAmount: totalCreditAmount,
    avgCreditPerJob: assignedJobs.length > 0 ? Math.round(totalCreditAmount / assignedJobs.length) : 0,
  };
}

export function useContractorScorecards(
  contractors: Contractor[],
  opportunities: ContractorOpportunity[],
  jobs: Job[],
  issues: Issue[],
  credits: CustomerCredit[] = [],
  marketplaceOffers: MarketplaceOffer[] = [],
): ContractorScorecard[] {
  return useMemo(
    () =>
      contractors
        .filter((c) => c.active)
        .map((c) => computeScorecard(c, opportunities, jobs, issues, credits, marketplaceOffers))
        .sort((a, b) => b.assignedJobCount - a.assignedJobCount || b.responseRate - a.responseRate),
    [contractors, opportunities, jobs, issues, credits, marketplaceOffers]
  );
}
